import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { BarChart3, Code2, RotateCcw, Save, AlertTriangle } from 'lucide-react';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  GET_TINYCMS_INTEGRATIONS,
  UPDATE_TINYCMS_INTEGRATIONS,
  RESTORE_TINYCMS_INTEGRATIONS,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';
import { GaConnectionPanel } from '../cms/AnalyticsConnect';
import { useAddons } from '../cms/addons';
import { SETTINGS_HISTORY_QUERY } from './SettingsHistory';
import { askInk } from '../cms/InkConfirm';

/**
 * Settings → Analytics & code. Tracking IDs (validated; we add the official
 * snippets) and the escape hatch: raw code at the end of <head> and <body> on
 * every public page. Admin-only, website-only (agents can't touch it), never
 * on previews, and every save is a version you can restore.
 *
 * The Free plan has no custom code and no Tag Manager (either could run a
 * script that removes the "Site powered by tinycms.ai" bar); those fields are
 * shown locked with a way to upgrade. GA4 and Amplitude stay open.
 */

interface Form {
  gtmId: string;
  ga4Id: string;
  amplitudeApiKey: string;
  amplitudeServerZone: 'US' | 'EU';
  headHtml: string;
  bodyHtml: string;
  consentMode: 'off' | 'banner';
  holdCustomUntilConsent: boolean;
}

const toForm = (v: any): Form => ({
  gtmId: v?.gtmId || '',
  ga4Id: v?.ga4Id || '',
  amplitudeApiKey: v?.amplitudeApiKey || '',
  amplitudeServerZone: v?.amplitudeServerZone === 'EU' ? 'EU' : 'US',
  headHtml: v?.headHtml || '',
  bodyHtml: v?.bodyHtml || '',
  consentMode: v?.consentMode === 'banner' ? 'banner' : 'off',
  holdCustomUntilConsent: v?.holdCustomUntilConsent !== false,
});

/** Fields that run arbitrary script: Free can't set them. */
const CUSTOM_CODE: Array<keyof Form> = ['gtmId', 'headHtml', 'bodyHtml'];

const PLAN_LINK = '/settings/billing';

/** Each ID's label, pattern placeholder, check (the server's own) and helper. */
const ID_FIELDS = {
  gtmId: {
    label: 'Container ID',
    placeholder: 'GTM-XXXXXXX',
    valid: (v: string) => /^GTM-[A-Z0-9]{4,12}$/.test(v.toUpperCase()),
    help: 'Starts with GTM-',
  },
  ga4Id: {
    label: 'Measurement ID',
    placeholder: 'G-XXXXXXXXXX',
    valid: (v: string) => /^G-[A-Z0-9]{4,15}$/.test(v.toUpperCase()),
    help: 'Starts with G-. In GA4: Admin › Data streams',
  },
  amplitudeApiKey: {
    label: 'API key',
    placeholder: '32-character API key',
    valid: (v: string) => /^[a-f0-9]{32}$/i.test(v),
    help: '32 characters. In Amplitude: Settings › Projects',
  },
} as const;
type IdKey = keyof typeof ID_FIELDS;
const ID_KEYS = Object.keys(ID_FIELDS) as IdKey[];

const inputClass =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary';
const codeClass = `${inputClass} font-mono text-xs min-h-[9rem] resize-y`;

export function AnalyticsSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { accountId } = useAccount();
  const { fullAnalytics } = useAddons();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_INTEGRATIONS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const current = data?.tinycmsIntegrations;
  const versions: any[] = data?.tinycmsIntegrationVersions || [];
  const canManage = Boolean(current?.canManage);
  // Reading the numbers back (Content → Analytics), separate from the tag itself.
  const gaConnection = current?.googleAnalytics?.connection || null;
  const gaAvailable = current?.googleAnalytics?.available !== false;
  // Older API without the flag: allowed, as before.
  const codeAllowed = current?.customCodeAllowed !== false;
  const loaded = useMemo(() => toForm(current), [current]);
  const [form, setForm] = useState<Form>(loaded);
  const [open, setOpen] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  // Set by a save attempt: from then on a malformed ID shows its helper in red.
  const [tried, setTried] = useState(false);
  useEffect(() => setForm(loaded), [loaded]);

  const [save, { loading: saving }] = useMutation(UPDATE_TINYCMS_INTEGRATIONS, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
  });
  const [restore] = useMutation(RESTORE_TINYCMS_INTEGRATIONS, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
  });

  const dirty = JSON.stringify(form) !== JSON.stringify(loaded);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const badId = (k: IdKey) => {
    const v = form[k].trim();
    return Boolean(v) && !ID_FIELDS[k].valid(v);
  };

  const handleSave = async () => {
    setMessage(null);
    setTried(true);
    if (ID_KEYS.some((k) => badId(k) && (codeAllowed || k !== 'gtmId'))) {
      setMessage({ tone: 'error', text: 'That doesn’t look right. Check the highlighted ID.' });
      return;
    }
    setTried(false);
    try {
      // On Free the code fields aren't sent (code kept from a paid plan stays as it is).
      const input: Partial<Form> = { ...form };
      if (!codeAllowed) CUSTOM_CODE.forEach((k) => delete input[k]);
      const { data: d } = await save({
        variables: { accountId, input, note: note.trim() || null },
      });
      setNote('');
      await refetch();
      setMessage({
        tone: 'ok',
        text: `Saved as version ${d.updateTinycmsIntegrations.version}. Live on your site now.`,
      });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const handleRestore = async (version: number) => {
    if (
      !(await askInk({
        title: `Restore version ${version}?`,
        body: 'It replaces what is live now, and is saved as a new version.',
        confirmLabel: 'Restore',
      }))
    )
      return;
    setMessage(null);
    try {
      await restore({ variables: { accountId, version } });
      await refetch();
      setMessage({ tone: 'ok', text: `Restored version ${version}.` });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  if (loading && !current) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;
  const disabled = !canManage;

  if (embedded) {
    const rows: Array<{ id: string; name: string; desc: string; value: string; accent: boolean }> =
      [
        {
          id: 'gtm',
          name: 'Google Tag Manager',
          desc: codeAllowed
            ? 'Sends tinycms.page_view on in-site navigation'
            : 'Needs a paid plan: a container can run any code',
          value: !codeAllowed ? 'Paid plans' : form.gtmId || 'Add container',
          accent: codeAllowed && !form.gtmId,
        },
        {
          id: 'ga4',
          name: 'Google Analytics 4',
          desc: gaConnection
            ? gaConnection.status === 'reconnect'
              ? 'Measurement ID · needs connecting again'
              : `Measurement ID · numbers shown ${fullAnalytics ? 'in Analytics' : 'on Today'}`
            : `Measurement ID · connect to see your numbers ${fullAnalytics ? 'here' : 'on Today'}`,
          value:
            gaConnection?.status === 'reconnect'
              ? 'Connect again'
              : form.ga4Id || (gaConnection ? 'Connected' : 'Add ID'),
          accent: !form.ga4Id || gaConnection?.status === 'reconnect',
        },
        {
          id: 'amp',
          name: 'Amplitude',
          desc: `API key · ${form.amplitudeServerZone} region`,
          value: form.amplitudeApiKey ? 'Key set' : 'Add key',
          accent: !form.amplitudeApiKey,
        },
        {
          id: 'consent',
          name: 'Cookie consent banner',
          desc: 'Tags wait until a visitor accepts',
          value: form.consentMode === 'banner' ? 'On' : 'Off',
          accent: false,
        },
        {
          id: 'code',
          name: 'Custom header & footer code',
          desc: codeAllowed ? 'Runs on every public page, exactly as written' : 'Needs a paid plan',
          value: !codeAllowed
            ? 'Paid plans'
            : form.headHtml || form.bodyHtml
              ? 'Edit'
              : 'None · Edit',
          accent: codeAllowed,
        },
      ];
    const locked = (id: string) => !codeAllowed && (id === 'gtm' || id === 'code');
    const idField = (k: IdKey) => {
      const f = ID_FIELDS[k];
      const bad = tried && badId(k);
      return (
        <label className="ink-site-label">
          {f.label}
          <input
            className="ink-site-input"
            value={form[k]}
            placeholder={f.placeholder}
            aria-invalid={bad || undefined}
            aria-describedby={`analytics-${k}-help`}
            style={bad ? { borderColor: 'var(--ink-red)' } : undefined}
            onChange={(e) => set(k, e.target.value)}
          />
          <span
            id={`analytics-${k}-help`}
            className="ink-form-hint"
            style={bad ? { color: 'var(--ink-red)' } : undefined}
          >
            {bad ? `That doesn’t look right. ${f.help}` : f.help}
          </span>
        </label>
      );
    };
    return (
      <div className="ink-site-card">
        {rows.map((row) => (
          <div key={row.id}>
            <button
              type="button"
              className="ink-site-row"
              style={{
                width: '100%',
                background: 'none',
                borderLeft: 0,
                borderRight: 0,
                borderTop: 0,
                cursor: canManage && !locked(row.id) ? 'pointer' : 'default',
                color: 'inherit',
                textAlign: 'left',
              }}
              onClick={() => canManage && !locked(row.id) && setOpen(open === row.id ? '' : row.id)}
            >
              <span>
                <span className="ink-site-row-title">{row.name}</span>
                <span className="ink-site-row-desc" style={{ display: 'block' }}>
                  {row.desc}
                </span>
              </span>
              <span
                style={{
                  font: '500 13px var(--ink-font)',
                  color: row.accent ? 'var(--ink-accent)' : 'var(--ink-ink3)',
                }}
              >
                {row.value}
              </span>
            </button>
            {open === row.id ? (
              <div style={{ padding: '0 0 14px', display: 'grid', gap: 8 }}>
                {row.id === 'gtm' ? idField('gtmId') : null}
                {row.id === 'ga4' ? (
                  <>
                    {idField('ga4Id')}
                    <GaConnectionPanel
                      accountId={accountId}
                      connection={gaConnection}
                      available={gaAvailable}
                      canManage={canManage}
                    />
                  </>
                ) : null}
                {row.id === 'amp' ? (
                  <>
                    {idField('amplitudeApiKey')}
                    <span className="ink-seg">
                      {(['US', 'EU'] as const).map((zone) => (
                        <button
                          key={zone}
                          type="button"
                          data-active={form.amplitudeServerZone === zone}
                          onClick={() => set('amplitudeServerZone', zone)}
                        >
                          {zone}
                        </button>
                      ))}
                    </span>
                  </>
                ) : null}
                {row.id === 'consent' ? (
                  <button
                    type="button"
                    className="ink-site-ask"
                    onClick={() =>
                      set('consentMode', form.consentMode === 'banner' ? 'off' : 'banner')
                    }
                  >
                    <span style={{ flex: 1 }}>
                      <span className="ink-site-row-title">Ask visitors for consent</span>
                      <span className="ink-site-row-desc" style={{ display: 'block' }}>
                        Shows a cookie banner. Tags wait until a visitor accepts.
                      </span>
                    </span>
                    <span className="ink-switch" data-on={form.consentMode === 'banner'} />
                  </button>
                ) : null}
                {row.id === 'code' ? (
                  <>
                    <label className="ink-site-label">
                      Header code
                      <textarea
                        className="ink-site-input"
                        rows={4}
                        value={form.headHtml}
                        onChange={(e) => set('headHtml', e.target.value)}
                        placeholder="End of <head>"
                      />
                    </label>
                    <label className="ink-site-label">
                      Footer code
                      <textarea
                        className="ink-site-input"
                        rows={4}
                        value={form.bodyHtml}
                        onChange={(e) => set('bodyHtml', e.target.value)}
                        placeholder="End of <body>"
                      />
                    </label>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
        {dirty && canManage ? (
          <div style={{ display: 'flex', gap: 8, padding: '4px 0 14px' }}>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="ink-btn" onClick={() => setForm(loaded)}>
              Discard
            </button>
          </div>
        ) : null}
        {message ? (
          <p
            className="ink-site-hint"
            style={{ color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)' }}
          >
            {message.text}
          </p>
        ) : null}
        {!codeAllowed ? (
          <p className="ink-site-hint">
            Custom code and Tag Manager come with a paid plan.{' '}
            <Link to={PLAN_LINK}>Change plan</Link>
          </p>
        ) : null}
        {!canManage ? (
          <p className="ink-site-hint">Only an account admin can change these.</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Analytics &amp; code
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          Measure your public site, or add your own code to every page. Nothing here runs on
          previews, so checking a draft never counts as a visit.
        </p>
        {!canManage ? (
          <p className="mt-2 text-sm text-cmp-ink-2">Only an account admin can change these.</p>
        ) : null}
      </div>

      <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-4">
        <h3 className="text-sm font-semibold">Tracking</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="gtm">Google Tag Manager container</Label>
            <input
              id="gtm"
              value={codeAllowed ? form.gtmId : ''}
              disabled={disabled || !codeAllowed}
              onChange={(e) => set('gtmId', e.target.value)}
              placeholder={codeAllowed ? ID_FIELDS.gtmId.placeholder : 'Needs a paid plan'}
              className={inputClass}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ga4">Google Analytics 4 measurement ID</Label>
            <input
              id="ga4"
              value={form.ga4Id}
              disabled={disabled}
              onChange={(e) => set('ga4Id', e.target.value)}
              placeholder="G-XXXXXXXXXX"
              className={inputClass}
            />
            <GaConnectionPanel
              accountId={accountId}
              connection={gaConnection}
              available={gaAvailable}
              canManage={canManage}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="amp">Amplitude API key</Label>
            <input
              id="amp"
              value={form.amplitudeApiKey}
              disabled={disabled}
              onChange={(e) => set('amplitudeApiKey', e.target.value)}
              placeholder={ID_FIELDS.amplitudeApiKey.placeholder}
              className={`${inputClass} font-mono`}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ampzone">Amplitude data region</Label>
            <select
              id="ampzone"
              value={form.amplitudeServerZone}
              disabled={disabled}
              onChange={(e) => set('amplitudeServerZone', e.target.value as 'US' | 'EU')}
              className={inputClass}
            >
              <option value="US">US</option>
              <option value="EU">EU</option>
            </select>
          </div>
        </div>
        {codeAllowed && form.gtmId && form.ga4Id ? (
          <p className="text-xs text-warning">
            Both GTM and GA4 are set. If your GTM container already sends to GA4, remove the GA4 ID
            here or every visit is counted twice.
          </p>
        ) : null}
        <p className="text-xs text-cmp-ink-3">
          In-site navigation sends a <code>tinycms.page_view</code> event to GTM’s data layer; GA4
          and Amplitude track it on their own.
        </p>
      </Card>

      <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="consent">Ask visitors for consent</Label>
            <p className="text-xs text-cmp-ink-2 mt-1">
              Shows a cookie banner. Until a visitor accepts, Google tags run with consent denied
              and Amplitude doesn’t load. Whether you need this depends on where your readers are.
            </p>
          </div>
          <Switch
            id="consent"
            checked={form.consentMode === 'banner'}
            disabled={disabled}
            onCheckedChange={(v) => set('consentMode', v ? 'banner' : 'off')}
          />
        </div>
        {form.consentMode === 'banner' && codeAllowed ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.holdCustomUntilConsent}
              disabled={disabled}
              onChange={(e) => set('holdCustomUntilConsent', e.target.checked)}
            />
            Also hold my custom code until the visitor accepts
          </label>
        ) : null}
      </Card>

      <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Code2 className="w-4 h-4" /> Custom code
        </h3>
        {codeAllowed ? (
          <p className="text-xs text-warning flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            This code runs on every page of your public site for every visitor, exactly as written.
            Only paste code from sources you trust.
          </p>
        ) : (
          <p className="text-sm text-cmp-ink-2">
            Custom code and Google Tag Manager come with a paid plan. Google Analytics and Amplitude
            work on Free.{' '}
            <Link to={PLAN_LINK} className="text-cmp-bronze underline">
              Change plan
            </Link>
          </p>
        )}
        <div className="space-y-1">
          <Label htmlFor="head">Header code (end of &lt;head&gt;)</Label>
          <textarea
            id="head"
            value={codeAllowed ? form.headHtml : ''}
            disabled={disabled || !codeAllowed}
            onChange={(e) => set('headHtml', e.target.value)}
            placeholder={'<meta name="google-site-verification" content="…">'}
            className={codeClass}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="body">Footer code (end of &lt;body&gt;)</Label>
          <textarea
            id="body"
            value={codeAllowed ? form.bodyHtml : ''}
            disabled={disabled || !codeAllowed}
            onChange={(e) => set('bodyHtml', e.target.value)}
            placeholder="<script>…</script>"
            className={codeClass}
          />
        </div>
      </Card>

      {canManage ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What changed?"
            aria-label="What changed (optional)"
            className={`${inputClass} max-w-sm`}
          />
          <Button onClick={handleSave} disabled={!dirty || saving}>
            <Save /> {saving ? 'Saving…' : 'Save'}
          </Button>
          {dirty ? (
            <Button variant="ghost" onClick={() => setForm(loaded)}>
              Discard changes
            </Button>
          ) : null}
        </div>
      ) : null}
      {message ? (
        <p className={`text-sm ${message.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {message.text}
        </p>
      ) : null}

      <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <h3 className="text-sm font-semibold mb-2">History</h3>
        {!versions.length ? (
          <p className="text-sm text-cmp-ink-2">No changes yet.</p>
        ) : (
          <ul className="divide-y divide-cmp-line">
            {versions.map((v) => (
              <li
                key={v.version}
                className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>
                  <span className="font-medium">v{v.version}</span>{' '}
                  <span className="text-cmp-ink-2">
                    {new Date(v.changedAt).toLocaleString()} · {v.changedBy || 'unknown'}
                    {v.note ? ` · ${v.note}` : ''}
                  </span>
                </span>
                {canManage && v.version !== current?.version ? (
                  <Button size="sm" variant="ghost" onClick={() => handleRestore(v.version)}>
                    <RotateCcw /> Restore
                  </Button>
                ) : v.version === current?.version ? (
                  <span className="text-xs text-cmp-ink-3">live</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
